/**
 * Accounting V2 expense register.
 *
 * This is the list the V2 engine actually reads and writes (`acc_expenses`),
 * distinct from the legacy tab which talks to /api/accounts/expenses. It carries
 * the filters, bulk actions and row-level lifecycle actions from Parts 6 and 12.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { PlusCircle, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useUser } from '@/shared/hooks/useAuth';
import {
  useExpensesV2,
  useExpenseAction,
  useDeleteExpenseV2,
  type Expense,
  type ExpenseFilters,
  type ExpenseStatus,
} from '../hooks/useExpenseApi';
import { formatMinor, formatShortDate } from '../utils/money';
import { ExpenseFormModal } from './ExpenseFormModal';

const STATUS_TONE: Record<ExpenseStatus, 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'> = {
  draft: 'neutral',
  submitted: 'warning',
  approved: 'info',
  rejected: 'danger',
  posted: 'primary',
  partially_paid: 'warning',
  paid: 'success',
  cancelled: 'neutral',
  refunded: 'neutral',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'posted', label: 'Posted' },
  { value: 'partially_paid', label: 'Partially paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
];

const SETTLEMENT_OPTIONS = [
  { value: '', label: 'Any funding' },
  { value: 'paid_cash', label: 'Cash' },
  { value: 'paid_bank', label: 'Bank' },
  { value: 'corporate_card', label: 'Corporate card' },
  { value: 'employee_paid', label: 'Employee paid' },
  { value: 'supplier_credit', label: 'Supplier credit' },
];

const PAYMENT_OPTIONS = [
  { value: '', label: 'Any payment state' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partially_paid', label: 'Partially paid' },
  { value: 'paid', label: 'Paid' },
];

const CELL = 'px-3 py-2 align-middle';
const HEAD = 'px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500';

/** Which lifecycle action a row offers next, given its status and user role. */
function nextAction(e: Expense, isEmployee?: boolean): { verb: 'submit' | 'approve' | 'post' | 'pay'; label: string } | null {
  if (['draft', 'rejected'].includes(e.status)) return { verb: 'submit', label: 'Submit' };
  if (isEmployee) return null;
  if (e.status === 'submitted') return { verb: 'approve', label: 'Approve' };
  if (e.status === 'approved') return { verb: 'post', label: 'Post' };
  if (['posted', 'partially_paid'].includes(e.status) && e.outstandingMinor > 0) return { verb: 'pay', label: 'Pay' };
  return null;
}

export function ExpenseListTab() {
  const user = useUser();
  const isEmployee = user?.role === 'employee';
  const [filters, setFilters] = useState<ExpenseFilters>({ page: 1, pageSize: 25 });
  const [searchDraft, setSearchDraft] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, isError, error } = useExpensesV2(filters);
  const action = useExpenseAction();
  const remove = useDeleteExpenseV2();

  const rows = data?.items ?? [];
  const meta = data?.meta ?? {};

  const set = (patch: Partial<ExpenseFilters>) =>
    setFilters((prev) => ({ ...prev, ...patch, page: patch.page ?? 1 }));

  const activeFilterCount = useMemo(
    () =>
      Object.entries(filters).filter(
        ([k, v]) => !['page', 'pageSize'].includes(k) && v !== undefined && v !== '',
      ).length,
    [filters],
  );

  const run = (id: string, verb: 'submit' | 'approve' | 'post' | 'pay', expense: Expense) => {
    const body =
      verb === 'approve' ? { level: expense.currentApprovalLevel || 1 }
        : verb === 'pay' ? { amountMinor: expense.outstandingMinor }
          : undefined;

    action.mutate({ id, action: verb, body }, {
      onSuccess: () => toast.success(`${expense.expenseNo} ${verb === 'pay' ? 'paid' : `${verb}ted`.replace('submitted', 'submitted')}`),
      onError: (e: unknown) => toast.error(getApiErrorMessage(e) || `Could not ${verb} the expense`),
    });
  };

  /** Bulk submit. Sequential so one failure does not abort the rest. */
  const bulkSubmit = async () => {
    const targets = rows.filter((e) => selected.has(e._id) && ['draft', 'rejected'].includes(e.status));
    if (!targets.length) {
      toast.error('Select one or more draft expenses first');
      return;
    }
    let ok = 0;
    const failures: string[] = [];
    for (const e of targets) {
      try {
        await action.mutateAsync({ id: e._id, action: 'submit' });
        ok += 1;
      } catch (err) {
        failures.push(`${e.expenseNo}: ${getApiErrorMessage(err) || 'failed'}`);
      }
    }
    setSelected(new Set());
    if (ok) toast.success(`Submitted ${ok} expense${ok === 1 ? '' : 's'}`);
    if (failures.length) toast.error(`${failures.length} failed. ${failures[0]}`);
  };

  const selectedTotal = rows
    .filter((e) => selected.has(e._id))
    .reduce((sum, e) => sum + e.grossAmountMinor, 0);

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex items-end gap-2">
          <Input
            label="Search"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') set({ search: searchDraft }); }}
            placeholder="Search by name, vendor, number, reference..."
            leftIcon={<Search className="h-4 w-4" aria-hidden="true" />}
            className="max-w-[280px]"
          />
          <Button variant="secondary" onClick={() => set({ search: searchDraft })}>Search</Button>
        </div>

        <Select
          label="Status"
          value={filters.status ?? ''}
          onChange={(e) => set({ status: e.target.value })}
          options={STATUS_OPTIONS}
          className="max-w-[170px]"
        />
        <Select
          label="Payment"
          value={filters.paymentStatus ?? ''}
          onChange={(e) => set({ paymentStatus: e.target.value })}
          options={PAYMENT_OPTIONS}
          className="max-w-[170px]"
        />
        <Select
          label="Funded by"
          value={filters.settlementMode ?? ''}
          onChange={(e) => set({ settlementMode: e.target.value })}
          options={SETTLEMENT_OPTIONS}
          className="max-w-[170px]"
        />
        <Input
          type="date"
          label="From"
          value={filters.startDate ?? ''}
          onChange={(e) => set({ startDate: e.target.value })}
          className="max-w-[150px]"
        />
        <Input
          type="date"
          label="To"
          value={filters.endDate ?? ''}
          onChange={(e) => set({ endDate: e.target.value })}
          className="max-w-[150px]"
        />

        {activeFilterCount > 0 ? (
          <Button
            variant="ghost"
            onClick={() => { setFilters({ page: 1, pageSize: 25 }); setSearchDraft(''); }}
          >
            <X className="mr-1 h-4 w-4" aria-hidden="true" /> Clear ({activeFilterCount})
          </Button>
        ) : null}

        <div className="ml-auto">
          <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
            <PlusCircle className="mr-1 h-4 w-4" aria-hidden="true" /> New expense
          </Button>
        </div>
      </div>

      {/* Bulk bar */}
      {selected.size > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-indigo-200 bg-indigo-50 px-3 py-2">
          <span className="text-sm text-slate-700">
            {selected.size} selected · {formatMinor(selectedTotal)}
          </span>
          <div className="flex gap-2">
            <Button size="sm" onClick={bulkSubmit} disabled={action.isPending}>Submit selected</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
          </div>
        </div>
      ) : null}

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : isError ? (
        <Card>
          <CardBody>
            <p className="text-sm font-medium text-rose-600">Could not load expenses.</p>
            <p className="mt-1 text-xs text-slate-500">{getApiErrorMessage(error) || 'Please retry in a moment.'}</p>
          </CardBody>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardBody>
            <div className="py-10 text-center">
              <p className="text-sm font-medium text-slate-600">
                {activeFilterCount > 0 ? 'No expenses match these filters' : 'No expenses captured yet'}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                {activeFilterCount > 0
                  ? 'Try clearing the filters.'
                  : 'Create one to start the draft → approve → post → pay flow.'}
              </p>
              {activeFilterCount === 0 ? (
                <Button className="mt-3" onClick={() => { setEditing(null); setFormOpen(true); }}>
                  <PlusCircle className="mr-1 h-4 w-4" aria-hidden="true" /> New expense
                </Button>
              ) : null}
            </div>
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200">
                <tr>
                  <th className={HEAD}>
                    <input
                      type="checkbox"
                      checked={selected.size === rows.length}
                      onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r._id)) : new Set())}
                      aria-label="Select all expenses on this page"
                    />
                  </th>
                  <th className={HEAD}>Expense</th>
                  <th className={HEAD}>Date</th>
                  <th className={HEAD}>Category</th>
                  <th className={HEAD}>Funded by</th>
                  <th className={HEAD}>Net</th>
                  <th className={HEAD}>Tax</th>
                  <th className={HEAD}>Gross</th>
                  <th className={HEAD}>Outstanding</th>
                  <th className={HEAD}>Status</th>
                  <th className={HEAD}>Flags</th>
                  <th className={HEAD} />
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => {
                  const next = nextAction(e, isEmployee);
                  return (
                    <tr key={e._id} className="border-b border-slate-100">
                      <td className={CELL}>
                        <input
                          type="checkbox"
                          checked={selected.has(e._id)}
                          onChange={() =>
                            setSelected((prev) => {
                              const n = new Set(prev);
                              if (n.has(e._id)) n.delete(e._id); else n.add(e._id);
                              return n;
                            })
                          }
                          aria-label={`Select ${e.expenseNo}`}
                        />
                      </td>
                      <td className={CELL}>
                        <Link to={`/accounting-v2/expenses/${e._id}`} className="font-mono text-xs text-indigo-600 hover:underline">
                          {e.expenseNo}
                        </Link>
                        {e.description ? <p className="max-w-[220px] truncate text-xs text-slate-500">{e.description}</p> : null}
                      </td>
                      <td className={`${CELL} whitespace-nowrap text-xs`}>{formatShortDate(e.expenseDate)}</td>
                      <td className={`${CELL} text-xs`}>{e.categoryLabel || '—'}</td>
                      <td className={`${CELL} text-xs`}>{e.settlementMode.replace(/_/g, ' ')}</td>
                      <td className={CELL}>{formatMinor(e.netAmountMinor, e.currency)}</td>
                      <td className={CELL}>{formatMinor(e.taxAmountMinor, e.currency)}</td>
                      <td className={`${CELL} font-medium`}>{formatMinor(e.grossAmountMinor, e.currency)}</td>
                      <td className={CELL}>
                        {e.outstandingMinor > 0
                          ? formatMinor(e.outstandingMinor, e.currency)
                          : <span className="text-xs text-slate-400">settled</span>}
                      </td>
                      <td className={CELL}>
                        <Badge tone={STATUS_TONE[e.status] ?? 'neutral'}>{e.status.replace(/_/g, ' ')}</Badge>
                      </td>
                      <td className={`${CELL} text-xs text-slate-500`}>
                        {[
                          e.isBillable ? 'billable' : null,
                          e.isReimbursable ? 'reimbursable' : null,
                          e.generatedByRecurrence ? 'recurring' : null,
                          e.refundedAmountMinor > 0 ? 'refunded' : null,
                        ].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className={CELL}>
                        <div className="flex justify-end gap-1.5">
                          {next ? (
                            <Button size="sm" variant="secondary" onClick={() => run(e._id, next.verb, e)} disabled={action.isPending}>
                              {next.label}
                            </Button>
                          ) : null}
                          {['draft', 'rejected'].includes(e.status) ? (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => { setEditing(e); setFormOpen(true); }}>
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  if (!window.confirm(`Delete ${e.expenseNo}? This cannot be undone.`)) return;
                                  remove.mutate(e._id, {
                                    onSuccess: () => toast.success(`${e.expenseNo} deleted`),
                                    onError: (err: unknown) => toast.error(getApiErrorMessage(err) || 'Delete failed'),
                                  });
                                }}
                                disabled={remove.isPending}
                              >
                                Delete
                              </Button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600">
            <span>
              Page {meta.page ?? 1} of {meta.totalPages ?? 1} · {meta.total ?? rows.length} expense(s)
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setFilters((p) => ({ ...p, page: Math.max(1, (p.page ?? 1) - 1) }))}
                disabled={(meta.page ?? 1) <= 1}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setFilters((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}
                disabled={(meta.page ?? 1) >= (meta.totalPages ?? 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Mounted only while open, and keyed by target, so the form seeds its
          state fresh on every open instead of resetting inside an effect. */}
      {formOpen ? (
        <ExpenseFormModal
          key={editing?._id ?? 'new'}
          open
          onClose={() => setFormOpen(false)}
          expense={editing}
        />
      ) : null}
    </div>
  );
}

export default ExpenseListTab;
